import { useEffect, useState } from "react";
import { previewPrompt } from "../../state/chat-actions.js";
import { useT } from "../../i18n/use-t.js";
import { contextUsage, type CompletionRequest } from "@roqeet/core";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * 프롬프트 미리보기 모달(결정 #12 디버깅 도구). 현재 대화 상태로 조립된, 모델에
 * 실제로 가는 요청을 그대로 보여준다. 카드/프리셋/로어북이 왜 그렇게 동작하는지
 * 확인하는 핵심 도구다. Roqeet 인라인 값·정규식·로어북·토큰 트림이 모두 반영된 결과.
 */
export function PromptPreview({ chatId, onClose }: { chatId: string; onClose: () => void }) {
  const { t } = useT();
  const [req, setReq] = useState<CompletionRequest | undefined>();
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose);

  useEffect(() => {
    let alive = true;
    void previewPrompt(chatId)
      .then((r) => {
        if (alive) setReq(r);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [chatId]);

  const usage = req ? contextUsage(req) : undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-2xl flex-col rounded-xl border border-line bg-bg-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-sm font-semibold text-ink">{t("prompt.title")}</span>
          <button className="text-ink-faint hover:text-ink" onClick={onClose} aria-label={t("chat.close")}>
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3 scroll-touch">
          {error ? (
            <p className="p-4 text-sm text-red-300">{error}</p>
          ) : !req ? (
            <p className="p-4 text-sm text-ink-faint">{t("prompt.assembling")}</p>
          ) : (
            <>
              {usage && usage.budget > 0 && (
                <p className="mb-2 text-xs text-ink-faint">
                  {t("prompt.usage", {
                    used: usage.used,
                    budget: usage.budget,
                    pct: Math.round(usage.ratio * 100),
                    over: usage.over ? t("prompt.usageOver") : "",
                  })}
                </p>
              )}
              {req.prompt != null ? (
                // 텍스트 컴플리션 스타일: 단일 문자열.
                <pre className="whitespace-pre-wrap break-words rounded-lg border border-line bg-bg-raised p-3 text-xs text-ink">
                  {req.prompt}
                </pre>
              ) : (
                <ul className="space-y-2">
                  {req.messages.map((m, i) => (
                    <li key={i} className="rounded-lg border border-line bg-bg-raised">
                      <div className="border-b border-line px-2 py-1 text-xs font-semibold text-accent-soft">
                        {m.role}
                        {m.name ? ` · ${m.name}` : ""}
                      </div>
                      <pre className="whitespace-pre-wrap break-words px-2 py-1.5 text-xs text-ink">
                        {m.content}
                      </pre>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
